import { type TElementSpecifier, toJQuery } from "@de-xima/xima-common-js-dom";
import { isEmpty, parseString } from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { doFilter } from "../core/Search.ts";
import { getWidgetByVar } from "../core/Util.ts";

/**
 * If any text is entered into the searchbar for the tree - ie. when a filter on the tree nodes
 * is active - disable the drag & drop behavior of the tree. Otherwise, enable it. When some
 * nodes are not visible due to filtering, drag & drop does not make sense as the drop points
 * of multiple nodes may now be collapsed.
 * @deprecated Use the new workflow engine.
 * @param selectorSearchbar Selector for the searchbar for the workflow tree.
 * @param treeWidgetVar Widget variable of the workflow tree widget.
 * @returns `true` if filtering is active, `false` otherwise.
 */
function checkDragDrop(selectorSearchbar: string, treeWidgetVar: string): boolean {
  const tree = getWidgetByVar(treeWidgetVar, PrimeFaces.widget.VerticalTree);

  if (tree === undefined) {
    return false;
  }

  const $searchbar = $(selectorSearchbar);
  const searchValue = $searchbar.find("input").addBack("input").val();

  // Disable drag&drop
  if (searchValue !== undefined && searchValue !== "") {
    $(".ui-wkfw-dragwarn").removeClass("Hidden");
    for (const droppable of tree.jq.find(".ui-tree-droppoint,.ui-treenode-droppable")) {
      const $droppable = $(droppable);
      if ($droppable.droppable("instance") !== undefined) {
        $droppable.droppable("destroy");
      }
    }
    for (const draggable of tree.jq.find(".ui-treenode-content")) {
      const $draggable = $(draggable);
      if ($draggable.draggable("instance") !== undefined) {
        $draggable.draggable("destroy");
      }
    }
    return true;
  }
  // Enable drag&drop

  $(".ui-wkfw-dragwarn").addClass("Hidden");
  if (tree.jq.find("span.ui-treenode-content").draggable("instance") === undefined) {
    // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
    tree["initDraggable"]();
    // biome-ignore lint/complexity/useLiteralKeys: We need to access a private member
    tree["initDroppable"]();
  }
  return false;
}

/**
 * Given the tree container, finds all nodes that represent a formcycle state.
 * @deprecated Use the new workflow engine.
 * @param $treeWrapper Container with the workflow tree.
 * @returns A map between the ID of each state and the tree node for that state.
 */
function findStateNodes($treeWrapper: JQuery): Map<string, JQuery> {
  const idStateMap = new Map<string, JQuery>();
  for (const label of $treeWrapper.find(".ui-tree .ui-treenode-state>.ui-treenode-content .ui-wkfw-node-label")) {
    const $label = $(label);
    const stateId = parseString($label.data("stateId"));
    if (isEmpty(stateId)) {
      continue;
    }
    const $treenode = $label.closest(".ui-treenode");
    if ($treenode.length !== 1) {
      continue;
    }
    idStateMap.set(stateId, $treenode);
  }
  return idStateMap;
}

/**
 * A child element is an element that is added as child of a node and is display as the last child of that node.
 * For example, the "add state" button displayed for each state node.
 * @deprecated Use the new workflow engine.
 * @param $treeWrapper Container with the workflow tree.
 */
function addChildElements($treeWrapper: JQuery): void {
  const idStateMap = findStateNodes($treeWrapper);
  // Add the "new action" buttons
  for (const element of $treeWrapper.find(".ui-wkfw-child-element-wrapper .ui-wkfw-child-element")) {
    const $element = $(element);
    const forStateId = parseString($element.data("forStateId"));
    const elementId = parseString($element.data("elementId"));
    if (isEmpty(forStateId)) {
      continue;
    }
    if (isEmpty(elementId)) {
      continue;
    }
    if (!idStateMap.has(forStateId)) {
      continue;
    }
    const $treenode = idStateMap.get(forStateId);
    if ($treenode !== undefined) {
      $element.addClass("ui-wkfw-child-element-appended");
      $treenode
        .children(".ui-wkfw-child-element-appended")
        .filter((_idx, element) => $(element).data("elementId") === elementId)
        .remove();
      const $newElement = $element.clone(true, true);
      const $button = $newElement.children("button");
      $button.attr("id", `${parseString($button.attr("id"))}:clone`);
      $treenode.append($newElement);
    } else {
      console.warn("No treenode found for state ", forStateId);
    }
  }
}

/**
 * Called when any modifications were made to the tree.
 * - Adds the buttons for the condition and error case next to the action
 * - Adds the button for creating a new action at the end of the actions of a state
 * - Makes sure drag&drop is disabled iff filtering is active.
 * @deprecated Use the new workflow engine.
 * @param treeWrapper Container with the workflow tree.
 */
export function onTreeRefresh(
  treeWrapper: TElementSpecifier<HTMLElement> = ".ui-wkfw-tree-wrapper",
  selectorSearchbar: string = ".ui-wkfw .searchbar",
  treeWidgetVar: string = "workflowTree",
): void {
  const $treeWrapper = toJQuery(treeWrapper);
  if ($treeWrapper.length === 1) {
    addChildElements($treeWrapper);
    updateChildElements($treeWrapper);
    checkDragDrop(selectorSearchbar, treeWidgetVar);
  }
}

/**
 * A child element is an element that is added as child of a node and is displayed as the last child of that node.
 * For example, the "add state" button displayed for each state node.
 * @deprecated Use the new workflow engine.
 * @param treeWrapper Tree widget wrapper to process.
 */
export function updateChildElements(treeWrapper: TElementSpecifier<HTMLElement> = ".ui-wkfw-tree-wrapper"): void {
  for (const element of toJQuery(treeWrapper).find(".ui-tree .ui-wkfw-child-element-appended")) {
    const $element = $(element);
    const $treenode = $element.closest(".ui-treenode.ui-treenode-state");
    if ($treenode.length === 1) {
      const $toggler = $treenode.children(".ui-treenode-content").find(".ui-tree-toggler");
      const hasChildren = $toggler.hasClass("ui-icon-triangle-1-e") || $toggler.hasClass("ui-icon-triangle-1-s");
      const isExpanded =
        hasChildren &&
        ($toggler.hasClass("ui-icon-triangle-1-s") || $treenode.children(".ui-treenode-children").is(":visible"));
      $element.removeClass("Hidden");
      if (hasChildren) {
        $element.addClass("ui-wkfw-has-children");
        $element.removeClass("ui-wkfw-childless");
      } else {
        $element.addClass("ui-wkfw-childless");
        $element.removeClass("ui-wkfw-has-children");
      }
      if (isExpanded) {
        $element.addClass("ui-wkfw-expanded");
        $element.removeClass("ui-wkfw-collapsed");
      } else {
        $element.addClass("ui-wkfw-collapsed");
        $element.removeClass("ui-wkfw-expanded");
      }
    }
  }
}

/**
 * Filter function for the tree searchbar. Does a normal client side filtering, and adds
 * some additional CSS tweaks to make the result look pretty. Also make sure to show the
 * parents when a child matches etc.
 * @deprecated Use the new workflow engine.
 * @param selectorSearchbar Selector for the searchbar of the workflow tree.
 * @param treeWidgetVar Widget variable for the workflow tree.
 */
export function filterTree(
  selectorSearchbar: string = ".ui-wkfw .context-sidebar .searchbar input",
  treeWidgetVar: string = "workflowTree",
): void {
  const tree = getWidgetByVar(treeWidgetVar, PrimeFaces.widget.BaseTree);
  if (tree === undefined) {
    return;
  }
  const items = doFilter($(selectorSearchbar));
  // Do not allow drag&drop when some nodes are invisible.
  const isFiltered = checkDragDrop(selectorSearchbar, treeWidgetVar);
  if (isFiltered) {
    // Add class for CSS tweaks.
    tree.jq.closest("form").addClass("fc-wkfw-filtered");
    const withExcluded = items.add(tree.jq.find(".ui-search-exclude"));
    // If a state is matching, also show its children.
    withExcluded
      .filter(".ui-treenode-state")
      .not(".data-entry-filtered")
      .find(".ui-treenode-action")
      .removeClass("data-entry-filtered");
    // Always show the parent state if an action is visible.
    withExcluded
      .filter(".ui-treenode-action")
      .not(".data-entry-filtered")
      .closest(".ui-treenode-state")
      .removeClass("data-entry-filtered");
  } else {
    // Remove class for CSS tweaks.
    tree.jq.closest("form").removeClass("fc-wkfw-filtered");
  }
}
